<section id="teach-features" class="py-24 bg-slate-50 border-t border-slate-200/60">
  <div class="container mx-auto px-6">
    
    <!-- Section Header -->
    <div class="text-center max-w-3xl mx-auto mb-16 space-y-4">
      <div class="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">
        <i class="fa-solid fa-wand-magic-sparkles"></i>
        <span>Built for Sri Lankan Educators</span>
      </div>
      <h2 class="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
        Everything You Need to Run & Grow Your Teaching Brand
      </h2>
      <p class="text-lg text-slate-600 leading-relaxed">
        Say goodbye to scattered links and manual tracking. Learnbird provides an all-in-one suite designed to power modern online tutoring.
      </p>
    </div>

    <!-- Feature Cards Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      
      <!-- Feature 1: Live Classes & Zoom -->
      <div class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1">
        <div class="daisy-card-body p-8 space-y-4">
          <div class="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold">
            <i class="fa-solid fa-video"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">Live Zoom Classes & Schedules</h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Set up recurring weekly live streams or custom workshops. Zoom OAuth integration automatically shares secure class links with enrolled students.
          </p>
          <div class="pt-2 text-xs font-semibold text-blue-600 flex items-center gap-1">
            <span>Automated meeting dispatch</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Feature 2: Public Tutor Directory Profile -->
      <div class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1">
        <div class="daisy-card-body p-8 space-y-4">
          <div class="w-14 h-14 rounded-2xl bg-purple-100 text-purple-600 flex items-center justify-center text-2xl font-bold">
            <i class="fa-solid fa-id-card"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">Public Tutor Profile & Directory</h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Get your own personalized profile page on our directory. Display your qualifications, intro video, subjects, and student ratings.
          </p>
          <div class="pt-2 text-xs font-semibold text-purple-600 flex items-center gap-1">
            <span>Stand out in tutor directory</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Feature 3: Self-Paced Courses & Video Playlists -->
      <div class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1">
        <div class="daisy-card-body p-8 space-y-4">
          <div class="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center text-2xl font-bold">
            <i class="fa-solid fa-film"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">Video Courses & Playlists</h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Upload pre-recorded lecture series, structure lesson playlists, and offer self-paced learning packages to complement your live classes.
          </p>
          <div class="pt-2 text-xs font-semibold text-emerald-600 flex items-center gap-1">
            <span>Monetize video content</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Feature 4: Study Material & Document Management -->
      <div class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1">
        <div class="daisy-card-body p-8 space-y-4">
          <div class="w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center text-2xl font-bold">
            <i class="fa-solid fa-file-pdf"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">Study Materials & Notes</h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Upload PDFs, past papers, presentation slides, and homework resources. Organize study content cleanly per course module.
          </p>
          <div class="pt-2 text-xs font-semibold text-amber-600 flex items-center gap-1">
            <span>Secure student file access</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Feature 5: WhatsApp & Telegram Community -->
      <div class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1">
        <div class="daisy-card-body p-8 space-y-4">
          <div class="w-14 h-14 rounded-2xl bg-cyan-100 text-cyan-600 flex items-center justify-center text-2xl font-bold">
            <i class="fa-solid fa-comments"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">Class Community Groups</h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Embed WhatsApp and Telegram group invite links visible only to enrolled students for announcements, Q&A, and quick discussions.
          </p>
          <div class="pt-2 text-xs font-semibold text-cyan-600 flex items-center gap-1">
            <span>Foster active learning groups</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

      <!-- Feature 6: Real-Time Earnings & Payout Analytics -->
      <div class="daisy-card bg-white shadow-sm hover:shadow-md border border-slate-200/80 rounded-2xl transition-all duration-300 hover:-translate-y-1">
        <div class="daisy-card-body p-8 space-y-4">
          <div class="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl font-bold">
            <i class="fa-solid fa-chart-pie"></i>
          </div>
          <h3 class="text-xl font-bold text-slate-900">Earnings & Subscription Dashboard</h3>
          <p class="text-slate-600 leading-relaxed text-sm">
            Monitor enrollments, recurring class subscriptions, and gross revenue with transparent billing reports and automated invoicing.
          </p>
          <div class="pt-2 text-xs font-semibold text-indigo-600 flex items-center gap-1">
            <span>Real-time income tracking</span>
            <i class="fa-solid fa-arrow-right"></i>
          </div>
        </div>
      </div>

    </div>
  </div>
</section>
